<script setup>
import { StockSummaryItem } from "@stockstotrade_com/stt-ts-shared-lib";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Fuse from "fuse.js";
import axios from "axios";
import MarketsHeroBanner from "@/components/markets/MarketsHeroBanner.vue";
import MarketsCoverageRow from "@/components/markets/MarketsCoverageRow.vue";
import MarketsMostPopular from "@/components/markets/MarketsMostPopular.vue";
import MarketsDeepDives from "@/components/markets/MarketsDeepDives.vue";
import MarketsTrends from "@/components/markets/MarketsTrends.vue";
import MarketsTrendingTopics from "@/components/markets/MarketsTrendingTopics.vue";
import MarketsWatchlistBanner from "@/components/markets/MarketsWatchlistBanner.vue";
import TopGainerStocks from "@/components/markets/TopGainerStocks.vue";
import QuoteFinder from "@/components/markets/QuoteFinder.vue";
import { useStocksStore } from "@/stores/useStocksStore";

// Extend dayjs with timezone and UTC plugins
dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.locale("en");
// Set default timezone - this should be done after plugins are loaded
dayjs.tz.setDefault("America/New_York");

const $route = useRoute();
const { current_hostname, hostname } = useRuntimeConfig().public;

// Remove dynamic state variables
const fuse = ref(null);
const results = ref([]);
const showModal = ref(false);
const marketIndexes = ref([]);
const stocksStore = useStocksStore();
const scrollContainer = ref(null);
const scrollPosition = ref(0);
const scrollMax = ref(0);

// Handle horizontal scrolling with mouse wheel
const handleHorizontalScroll = (event) => {
	if (scrollContainer.value && breakpoints.isGreaterOrEqual("lg")) {
		// Convert vertical scroll to horizontal scroll
		scrollContainer.value.scrollLeft += event.deltaY;
	}
};

// Update scroll position on scroll event
const updateScrollState = () => {
	if (scrollContainer.value) {
		scrollPosition.value = scrollContainer.value.scrollLeft;
		const { scrollWidth, clientWidth } = scrollContainer.value;
		scrollMax.value = scrollWidth - clientWidth;
	}
};

// Scroll control functions
const canScrollLeft = computed(() => {
	return scrollPosition.value > 0;
});

const canScrollRight = computed(() => {
	return scrollPosition.value < scrollMax.value - 1; // -1 for rounding errors
});

const scrollLeft = () => {
	if (scrollContainer.value) {
		const scrollAmount = scrollContainer.value.clientWidth * 0.8;
		scrollContainer.value.scrollBy({
			left: -scrollAmount,
			behavior: "smooth"
		});
	}
};

const scrollRight = () => {
	if (scrollContainer.value) {
		const scrollAmount = scrollContainer.value.clientWidth * 0.8;
		scrollContainer.value.scrollBy({
			left: scrollAmount,
			behavior: "smooth"
		});
	}
};
// Markets trends data moved to component

// Removed trending topics data - now managed in component

const breakpoints = useBreakpoints({
	md: 1024,
	lg: 1280,
	xl: 1520,
	xxl: 1690
});

const isDesktop = computed(() => breakpoints.isGreaterOrEqual("lg"));
const isDesktopWide = computed(() => breakpoints.isGreater("xl"));

const { data, refresh: refreshNewsData } = await useAsyncData(
	"news_page",
	async ({ $config }) => {
		try {
			const hostname = $config.public.hostname;
			const current_hostname = $config.public.current_hostname;

			// Prepare headers for SSR
			let headers = {};
			if (import.meta.server) {
				console.log("[X-Original-Origin] - parallel fetch", current_hostname);
				headers = { "X-Original-Origin": current_hostname };
			}

			// On the server (Cloudflare Worker), calling songbirdProxy would create a
			// self-referential subrequest back to the same Worker, which is slow and
			// unreliable. Instead, go directly to the Songbird backend via the private
			// songbirdUrl (not exposed to the client).
			const songbirdBase = import.meta.server
				? $config.songbirdUrl + "api/"
				: $config.public.songbirdProxy;

			// Fetch WordPress SEO data and news list in parallel.
			// Using Promise.allSettled so a WordPress failure never blocks the news list.
			const [wpResult, newsResult] = await Promise.allSettled([
				axios.get(`${hostname}wp-json/wp/v2/pages/23037/`, { timeout: 10000 }),
				axios.get(`${songbirdBase}stocks/news`, {
					params: { limit: 20 },
					headers,
					timeout: 25000
				})
			]);

			if (wpResult.status === "rejected") {
				console.warn(
					"[news/index.vue] WordPress fetch failed:",
					wpResult.reason?.message || wpResult.reason
				);
			}
			if (newsResult.status === "rejected") {
				console.warn(
					"[news/index.vue] News list fetch failed:",
					newsResult.reason?.message || newsResult.reason
				);
			}

			const wpData = wpResult.status === "fulfilled" ? wpResult.value?.data : null;
			const news_list_response =
				newsResult.status === "fulfilled"
					? newsResult.value
					: { data: { items: [] } };

			const yoast_head_json = wpData?.yoast_head_json ?? null;
			const yoast_meta = wpData?.yoast_meta ?? null;
			const schema_json_raw = wpData?.yoast_head_json?.schema_json ?? null;

			let parsed_schema;
			if (schema_json_raw) {
				parsed_schema = JSON.parse(schema_json_raw);
				const parsed_schema_id = parse_saswpSchema(
					parsed_schema["@graph"],
					hostname,
					current_hostname
				);

				parsed_schema["@graph"] = parsed_schema_id;
			}

			const schema_stuff = {
				"@context": "https://schema.org",
				"@graph": [
					{
						"@type": ["WebPage", "CollectionPage"],
						"@id": current_hostname + "news/",
						url: current_hostname + "news/",
						name: "StocksToTrade | News",
						isPartOf: {
							"@id": current_hostname + "#website"
						},
						primaryImageOfPage: {
							"@id": "https://content.stockstotrade.com/news/#primaryimage"
						},
						image: {
							"@id": hostname + "news/#primaryimage"
						},
						thumbnailUrl:
							"https://content.stockstotrade.com//images/block-ready-bg.jpg",
						datePublished: "2024-09-12T10:10:00Z",
						dateModified: "2024-09-12T10:10:00Z",
						description: `StocksToTrade News is your go-to source for real-time stock market updates, laser-focused on the key catalysts driving short-term price action. Designed for active traders and investors, we specialize in delivering actionable insights on volatile sectors—such as penny stocks—where rapid price movements create high-potential opportunities..`,
						breadcrumb: {
							"@id": current_hostname + "news/#breadcrumb"
						},
						inLanguage: "en-US"
					},
					{
						"@type": "ImageObject",
						inLanguage: "en-US",
						"@id": hostname + "news/#primaryimage",
						url: hostname + "images/block-ready-bg.jpg",
						contentUrl:
							"https://content.stockstotrade.com//images/block-ready-bg.jpg",
						width: 1000,
						height: 721
					},
					{
						"@type": "BreadcrumbList",
						"@id": current_hostname + "news/#breadcrumb",
						itemListElement: [
							{
								"@type": "ListItem",
								position: 1,
								name: "Home",
								item: current_hostname + ""
							},
							{ "@type": "ListItem", position: 2, name: "News &#8211; Timothy Sykes" }
						]
					},
					{
						"@type": "WebSite",
						"@id": current_hostname + "#website",
						url: current_hostname + "",
						name: "Timothy Sykes",
						description: "How To Trade Penny Stocks Without The Stock Promoter BS",
						publisher: {
							"@id":
								current_hostname + "#/schema/person/9acd0cdbfa16f9846109176e93fd4ac4"
						},

						inLanguage: "en-US"
					},
					{
						"@type": ["Person", "Organization"],
						"@id":
							current_hostname + "#/schema/person/9acd0cdbfa16f9846109176e93fd4ac4",
						name: "Timothy Sykes",
						image: {
							"@type": "ImageObject",
							inLanguage: "en-US",
							"@id": hostname + "#/schema/person/image/",
							url: "https://content.timothysykes.com/wp-content/uploads/2016/09/timothy-sykes-665x385.jpg",
							contentUrl:
								"https://content.timothysykes.com/wp-content/uploads/2016/09/timothy-sykes-665x385.jpg",
							width: 665,
							height: 385,
							caption: "Timothy Sykes"
						},
						logo: {
							"@id": hostname + "#/schema/person/image/"
						},
						sameAs: [
							current_hostname + "blog/author/timothysykes/",
							"https://x.com/timothysykes",
							"https://news.google.com/publications/CAAqBwgKMJbmngswsvC2Aw?hl=en-US&gl=US&ceid=US%3Aen"
						]
					}
				]
			};

			return {
				yoast_head_json,
				yoast_meta,
				schema_json: schema_stuff,
				news_list: news_list_response.data?.items || []
			};
		} catch (err) {
			console.error("Error in news index data fetch:", err);
			// Return default values to prevent 500 errors
			return {
				yoast_head_json: null,
				yoast_meta: null,
				schema_json: null,
				news_list: []
			};
		}
	}
);

// Reactive computed properties from useAsyncData (needed for SPA navigation)
const news_list_data = computed(() => data.value?.news_list || []);
const yoast_head_json = computed(() => data.value?.yoast_head_json || null);
const schema_json = computed(() => data.value?.schema_json || null);

function closeModal() {
	showModal.value = false;
	results.value = [];
	document.body.style.overflow = ""; // restore scroll
}

function handleKeyDown(e) {
	if (e.key === "Escape") {
		closeModal();
	}
}

// WebSocket connection for S&P 500 streaming data
const sp500Ws = ref(null);
const sp500WsReconnectTimer = ref(null);
const sp500WsReconnectAttempts = ref(0);
const SP500_WS_MAX_RECONNECT_ATTEMPTS = 10;
const SP500_WS_BASE_DELAY = 1000; // 1 second base delay for exponential backoff
const marketIndexesLoadFailed = ref(false);

/**
 * Process S&P 500 stock data from the WebSocket and update marketIndexes.
 */
const processSP500Data = (stocks) => {
	// Sort by percentage change descending (highest gain first)
	const sortedStocks = [...stocks].sort(
		(a, b) => (b.PC_CHG || 0) - (a.PC_CHG || 0)
	);

	marketIndexes.value = sortedStocks.map((stock) => ({
		symbol: stock.SYMBOL,
		name: stock.NAME || stock.SYMBOL,
		price: stock.LAST || 0,
		change: stock.NET_CHG || 0,
		percentChange: stock.PC_CHG || 0
	}));
};

/**
 * Connect to the S&P 500 WebSocket stream with exponential backoff reconnection.
 */
const connectSP500WebSocket = async () => {
	if (!import.meta.client) return;

	// Fetch the WS URL from the server-side endpoint (keeps the backend URL out of page HTML)
	let songbirdWsUrl;
	try {
		const res = await $fetch("/api/ws-config");
		songbirdWsUrl = res.wsUrl;
	} catch (err) {
		console.warn("[SP500 WS] Failed to fetch WS config, skipping WebSocket", err);
		if (!marketIndexes.value.length) marketIndexesLoadFailed.value = true;
		return;
	}

	if (!songbirdWsUrl) {
		console.warn("[SP500 WS] songbirdWsUrl not configured, skipping WebSocket");
		if (!marketIndexes.value.length) marketIndexesLoadFailed.value = true;
		return;
	}

	// Build WebSocket URL: songbirdWsUrl ends with "/" (e.g. ws://localhost:5110/)
	const wsUrl = `${songbirdWsUrl}ws/index-data?index=sp500`;

	try {
		const ws = new WebSocket(wsUrl);

		ws.onopen = () => {
			console.log("[SP500 WS] Connected to index data stream");
			sp500WsReconnectAttempts.value = 0; // Reset backoff on successful connect
		};

		ws.onmessage = (event) => {
			try {
				const msg = JSON.parse(event.data);

				switch (msg.type) {
					case "connected":
						console.log(
							"[SP500 WS] Market status:",
							msg.marketStatus,
							"| Streaming:",
							msg.isStreaming
						);
						break;

					case "index_update":
						if (msg.data && Array.isArray(msg.data)) {
							processSP500Data(msg.data);
							// Update scroll state after data arrives
							nextTick().then(() => updateScrollState());
						}
						break;

					case "status":
						console.log("[SP500 WS] Status:", msg.message);
						if (msg.nextActiveTime) {
							console.log("[SP500 WS] Next active:", msg.nextActiveTime);
						}
						break;

					case "error":
						console.error("[SP500 WS] Server error:", msg.error);
						break;

					case "pong":
						// Keep-alive response, no action needed
						break;
				}
			} catch (err) {
				console.error("[SP500 WS] Error parsing message:", err);
			}
		};

		ws.onclose = () => {
			console.log("[SP500 WS] Disconnected");
			sp500Ws.value = null;
			scheduleSP500Reconnect();
		};

		ws.onerror = (error) => {
			console.error("[SP500 WS] Connection error:", error);
			// onclose will fire after onerror, reconnection handled there
		};

		sp500Ws.value = ws;
	} catch (error) {
		console.error("[SP500 WS] Failed to create WebSocket:", error);
		scheduleSP500Reconnect();
	}
};

/**
 * Schedule a reconnection attempt with exponential backoff.
 */
const scheduleSP500Reconnect = () => {
	if (sp500WsReconnectAttempts.value >= SP500_WS_MAX_RECONNECT_ATTEMPTS) {
		console.warn("[SP500 WS] Max reconnection attempts reached, giving up");
		if (!marketIndexes.value.length) marketIndexesLoadFailed.value = true;
		return;
	}

	const delay = Math.min(
		SP500_WS_BASE_DELAY * Math.pow(2, sp500WsReconnectAttempts.value),
		30000 // cap at 30 seconds
	);

	console.log(
		`[SP500 WS] Reconnecting in ${delay}ms (attempt ${sp500WsReconnectAttempts.value + 1}/${SP500_WS_MAX_RECONNECT_ATTEMPTS})`
	);

	sp500WsReconnectTimer.value = setTimeout(() => {
		sp500WsReconnectAttempts.value++;
		connectSP500WebSocket();
	}, delay);
};

/**
 * Close the S&P 500 WebSocket and cancel any pending reconnection.
 */
const closeSP500WebSocket = () => {
	if (sp500WsReconnectTimer.value) {
		clearTimeout(sp500WsReconnectTimer.value);
		sp500WsReconnectTimer.value = null;
	}

	if (sp500Ws.value) {
		// Set onclose to null to prevent reconnection after intentional close
		sp500Ws.value.onclose = null;
		sp500Ws.value.close();
		sp500Ws.value = null;
	}

	sp500WsReconnectAttempts.value = 0;
};

const computed_news_list = computed(() => {
	if (!news_list_data.value || news_list_data.value.length === 0) {
		return [];
	}

	return (
		news_list_data.value.map((item) => ({
			date: item.date,
			title: item.title,
			content_read_time: item.content_read_time,
			stock: item.stock_pairs?.[1] || null,
			stock_pairs: item.stock_pairs,
			image: item.image,
			slug: `/news/${item.slug}/`,
			link: `/news/${item.slug}/`
		})) || []
	);
});

onMounted(async () => {
	if (import.meta.client) {
		// If SSR left news list empty (e.g. fetch failed on Workers), refetch on client
		if (news_list_data.value.length === 0) {
			await refreshNewsData();
		}

		// Subscribe to stocks from news list if data is already available
		if (news_list_data.value.length > 0) {
			news_list_data.value.forEach((item) => {
				if (item.stock_pairs?.length > 0) {
					stocksStore.addSubscribedSymbolsFromPairs(item.stock_pairs);
				}
			});
		}

		// Connect to S&P 500 WebSocket stream (replaces HTTP polling)
		connectSP500WebSocket();

		// Update scroll state after initial render
		await nextTick();
		updateScrollState();

		try {
			// Validate that we have valid data before making API calls
			if (
				!most_active ||
				typeof most_active !== "object" ||
				Object.keys(most_active).length === 0
			) {
				console.warn("most_active is invalid or empty, skipping API calls");
			} else {
				const [stocks, stocks_historic] = await Promise.all([
					fetchMostActiveStocks(Object.keys(most_active)),
					fetchHistoricData(Object.keys(most_active))
				]);

				const payload = stocks.map((stock) => {
					const historicData = stocks_historic?.[stock.SYMBOL];

					return {
						...stock,
						prices: historicData ? historicData.map((item) => item.close) : [],
						labels: historicData ? historicData.map((item) => item.time) : []
					};
				});

				if (import.meta.client) {
					sessionStorage.setItem("mostActiveStocks", JSON.stringify(payload));
					sessionStorage.setItem("mostActiveStocksTimestamp", Date.now().toString());
				}

				stocksStore.setMostActiveStocks(payload);

				if (isMarketOpen()) {
					const most_active_interval = setInterval(async () => {
						if (!isMarketOpen()) {
							clearInterval(most_active_interval);
							intervalId.value = null;
							return;
						}

						try {
							if (
								!most_active ||
								typeof most_active !== "object" ||
								Object.keys(most_active).length === 0
							) {
								return;
							}

							const stocks = await fetchMostActiveStocks(Object.keys(most_active));

							const currentMostActive = stocksStore.mostActiveStocks || [];

							const updatedPayload = stocks.map((stock) => {
								const existingStock = currentMostActive.find(
									(existing) => existing.SYMBOL === stock.SYMBOL
								);

								return {
									...existingStock,
									...stock
								};
							});

							sessionStorage.setItem(
								"mostActiveStocks",
								JSON.stringify(updatedPayload)
							);
							sessionStorage.setItem(
								"mostActiveStocksTimestamp",
								Date.now().toString()
							);

							stocksStore.setMostActiveStocks(updatedPayload);
						} catch (error) {
							console.error("Error updating most active stocks:", error);
						}
					}, 10000);

					intervalId.value = most_active_interval;
				}
			}
		} catch (error) {
			console.warn(
				"[news/index] Failed to fetch stock data:",
				error?.message || error
			);
		}

		// Setup scroll listener for arrow buttons
		if (scrollContainer.value) {
			scrollContainer.value.addEventListener("scroll", updateScrollState);
			updateScrollState();
		}
	}
});

// Clean up WebSocket and listeners when component unmounts
onUnmounted(() => {
	closeSP500WebSocket();
	if (scrollContainer.value) {
		scrollContainer.value.removeEventListener("scroll", updateScrollState);
	}
});

// Watch for data changes to handle SPA navigation (when data arrives after mount)
watch(
	() => data.value,
	async (newData) => {
		if (!import.meta.client || !newData) return;

		// Subscribe to stocks from news list
		if (newData.news_list?.length > 0) {
			newData.news_list.forEach((item) => {
				if (item.stock_pairs?.length > 0) {
					stocksStore.addSubscribedSymbolsFromPairs(item.stock_pairs);
				}
			});
		}
	},
	{ immediate: false }
);

// Watch for route changes and cleanup stocks data when navigating away from news page
watch(
	() => $route.path,
	async (newPath, oldPath) => {
		if (import.meta.client && newPath !== oldPath && oldPath === "/news") {
			// Only cleanup when navigating away from news pages entirely, not to other news pages
			if (!newPath?.startsWith("/news/")) {
				stocksStore.cleanup();
			}
		}
	}
);

// Clean up WebSocket on route change
watch(
	() => $route.path,
	(newPath, oldPath) => {
		// Close WebSocket when navigating away from the news index page
		if (import.meta.client && newPath !== oldPath && oldPath === "/news") {
			closeSP500WebSocket();
		}
	}
);

watch(
	() => stocksStore.data,
	(newData, _) => {
		if (import.meta.client) {
			fuse.value = new Fuse(newData || [], {
				keys: ["symbol", "longName"],
				threshold: 0.2,
				includeScore: false,
				findAllMatches: true,
				isCaseSensitive: false
			});

			document.addEventListener("keydown", handleKeyDown);
		}
	},
	{
		deep: true,
		immediate: true
	}
);

// Watch for marketIndexes changes to update scroll state
watch(
	() => marketIndexes.value,
	() => {
		nextTick(() => {
			updateScrollState();
		});
	}
);

// Markets trends tab handling moved to component

// Trending topics tab handling moved to component

const head_seo = computed(() => {
	if (yoast_head_json.value) {
		const filtered = Object.entries(yoast_head_json.value)
			.filter(
				(item) =>
					![
						"title",
						"robots",
						"canonical",
						"schema_json",
						"description",
						"og_description",
						"WordPressAuthor_YoastHeadJson_Schema_"
					].includes(item[0])
			)
			.map((item) => {
				if (
					item[0] === "og_image" &&
					Array.isArray(item[1]) &&
					item[1].length > 0
				) {
					return [
						item[0],
						[
							{ ...item[1][0], url: current_hostname + "images/block-ready-bg.jpg" },
							...item[1].slice(1)
						]
					];
				}

				return item;
			});

		return filtered;
	}

	return null;
});

useHead(() => ({
	script: [
		{
			type: "application/ld+json",
			class: "saswp-schema-markup-output",
			innerHTML: JSON.stringify(schema_json.value)
		}
	],
	link: [
		{
			rel: "preconnect",
			href: hostname
		}
	]
}));
// Trending topics state moved to component
</script>
<template>
	<Head>
		<Link
			rel="preconnect"
			href="https://stt-ts-resources.s3.us-east-1.amazonaws.com"
		/>
		<Title>StocksToTrade | News</Title>
		<Link
			rel="canonical"
			:href="current_hostname + 'news/'"
		/>

		<Meta
			name="description"
			content="StocksToTrade News is your go-to source for real-time stock market updates, laser-focused on the key catalysts driving short-term price action."
		/>
		<Meta
			property="og:description"
			content="StocksToTrade News is your go-to source for real-time stock market updates, laser-focused on the key catalysts driving short-term price action."
		/>

		<Meta
			property="og:image"
			:content="current_hostname + 'images/tt-news-image.jpg'"
		/>
		<Meta
			property="og:image:width"
			content="1200"
		/>
		<Meta
			property="og:image:height"
			content="630"
		/>

		<template v-if="head_seo">
			<template
				v-for="(tag, i) in head_seo"
				:key="i"
			>
				<Meta
					v-if="tag[0].startsWith('og_') && tag[0] !== 'og_image'"
					:property="'og:' + tag[0].slice(3)"
					:content="tag[1][0].url || tag[1]"
				/>

				<Meta
					v-else-if="tag[0].startsWith('article')"
					:property="'article:' + tag[0].slice(8)"
					:content="tag[1]"
				/>
				<Meta
					v-else-if="tag[0] !== 'twitter_misc' && tag[0].startsWith('twitter')"
					:name="'twitter:' + tag[0].slice(8)"
					:content="tag[1]"
				/>

				<template v-else-if="tag[0] === 'twitter_misc'">
					<Meta
						:name="'twitter:label1'"
						:content="Object.keys(tag[1])[0]"
					/>
					<Meta
						:name="'twitter:data2'"
						:content="Object.values(tag[1])[0]"
					/>
					<Meta
						:name="'twitter:label2'"
						:content="Object.keys(tag[1])[1]"
					/>
					<Meta
						:name="'twitter:data1'"
						:content="Object.values(tag[1])[1]"
					/>
				</template>

				<Meta
					v-else-if="tag[0] != 'og_image'"
					:name="tag[0]"
					:content="tag[1]"
				/>
			</template>
		</template>
	</Head>
	<section
		id="most-active-header"
		class="overflow-hidden bg-[#F6F8FA] border-b border-slate-50"
	>
		<Container class="mx-auto !px-0 relative">
			<template v-if="canScrollLeft">
				<div
					class="hidden lg:block absolute left-0 top-0 bottom-0 w-full lg:max-w-[120px] max-w-[60px] bg-gradient-to-r from-[#F6F8FA] to-transparent pointer-events-none"
				></div>

				<div
					class="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10"
				>
					<button
						class="hidden w-12 h-12 rounded-full lg:flex items-center border justify-center transition-transform duration-150 active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed bg-white border-[#D1D5DB]"
						@click="scrollLeft"
					>
						<svg
							width="18"
							height="18"
							viewBox="0 0 18 18"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M11.25 13.5L6.75 9L11.25 4.5"
								stroke="#666D80"
								stroke-width="1.125"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
					</button>
				</div>
			</template>

			<!-- S&P 500 Top Gainers - Lateral Scroll -->
			<div
				ref="scrollContainer"
				class="flex gap-x-4 lg:gap-x-3 gap-y-2 py-2 lg:py-3 px-4 lg:px-0 overflow-x-auto scrollbar-hide scroll-smooth lg:pr-16"
				@wheel.prevent="handleHorizontalScroll"
			>
			<template v-if="!marketIndexes?.length && !marketIndexesLoadFailed">
				<div
					v-for="i in 10"
					:key="i"
					class="flex w-44 rounded-lg h-14 items-center justify-center gap-2 flex-shrink-0 animate-pulse bg-primary-500/10"
				></div>
			</template>

			<div
				v-else-if="marketIndexesLoadFailed && !marketIndexes?.length"
				class="flex items-center justify-center w-full py-3 text-sm text-gray-400"
			>
				This data is not available right now, try again later.
			</div>

			<ClientOnly v-else>
				<div
					v-for="stock in marketIndexes"
					:key="stock.symbol"
					class="flex-shrink-0"
				>
					<StockSummaryItem
						:symbol="stock.symbol"
						:net_chg="stock.change"
						:price="stock.price"
						:pc_chg="stock.percentChange"
						:name="stock.name"
						type="inline"
					/>
				</div>
			</ClientOnly>
			</div>

			<!-- Right gradient overlay -->
			<div
				class="hidden lg:block absolute right-0 top-0 bottom-0 w-full max-w-[60px] lg:max-w-[120px] bg-gradient-to-l from-[#F6F8FA] to-transparent pointer-events-none"
			></div>

			<!-- Arrow buttons -->
			<div
				v-if="canScrollRight"
				class="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10"
			>
				<button
					class="hidden w-12 h-12 rounded-full lg:flex items-center border justify-center transition-transform duration-150 active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed bg-white border-[#D1D5DB]"
					@click="scrollRight"
				>
					<svg
						width="18"
						height="18"
						viewBox="0 0 18 18"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M6.75 13.5L11.25 9L6.75 4.5"
							stroke="#666D80"
							stroke-width="1.125"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>
			</div>
		</Container>
	</section>

	<section class="bg-white py-4 lg:py-9 relative flex flex-col items-center">
		<ClientOnly><MarketsAnchorMenu v-if="isDesktopWide" /></ClientOnly>
		<Container
			id="markets-page"
			class="flex flex-col xl:grid lg:grid-cols-12 gap-4"
		>
			<div class="xl:col-span-9 flex flex-col gap-6">
				<MarketsHeroBanner
					id="featured"
					class="xl:mb-[64px]"
					:news_list="computed_news_list.slice(0, 4)"
				/>
				<MarketsCoverageRow
					id="coverage"
					:news="computed_news_list.slice(4, 10)"
				/>

				<ClientOnly>
					<template v-if="!isDesktop">
						<WatchLiveStream />
						<TopGainerStocks id="top-gainers-header" />
						<EarningsCalendar />
					</template>
				</ClientOnly>

				<div
					class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:mb-8"
					id="popular"
				>
					<MarketsMostPopular :news="computed_news_list.slice(10, 16)" />
					<MarketsDeepDives />
				</div>

				<MarketsWatchlistBanner class="mb-8 lg:hidden" />

				<MarketsTrends id="trends" />
				<MarketsWatchlistBanner class="mb-8 hidden lg:block" />
				<MarketsTrendingTopics
					id="topics"
					:tabs="[
						'AI & Tech',
						'Real Estate',
						'Energy',
						'Healthcare',
						'Consumer Goods',
						'Industrials'
					]"
				/>
			</div>

			<ClientOnly>
				<div
					class="col-span-3 flex flex-col gap-6"
					v-if="isDesktop"
				>
					<WatchLiveStream />
					<QuoteFinder />
					<TopGainerStocks id="top-gainers-header" />
					<EarningsCalendar />
				</div>
			</ClientOnly>
		</Container>
	</section>
</template>
